/**
 * Cart Summary Utility Functions
 *
 * Provides helper functions for calculating cart totals and filtering data.
 */
import {
  COUPONS_EXEMPT_FROM_LIMIT,
  getCouponDisplayName,
  APP_EXCLUSIVE_COUPON_CODES,
  ProductType,
} from '../../constants';
import type { CartItemResponse, CartCouponResponse, CartResponseResponse } from '../../model';
import { getFilteredHalloweenPromoItems } from '../campaign';

type Cart = CartResponseResponse['cart'];

export const calculateTotalQuantity = (items: CartItemResponse[]): number => {
  return items.reduce((prev, curr) => prev + curr.quantity, 0);
};

const getPackNumber = (packStr?: string): number => {
  if (!packStr) {
    return 1;
  }
  const match = packStr.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 1;
};

/**
 * Physical unit count as declared to customs, closing the pack expansion gap (SSD-5767):
 * customs limits such as the YUN Express 6-unit cap for CL/CO count pack-expanded physical
 * units, while totalQuantity counts cart lines - so 3 x "3 Pack" reads as 3 in the cart but
 * ships 9 declared units. Mirrors Facade's ProductQuantityCalculator: only Variation products
 * expand (quantity x pack), pack is the first number in the pack attribute string
 * (e.g. "3 Pack" -> 3), missing or non-numeric pack counts as 1.
 */
export const calculateTotalPackExpandedQuantity = (items: CartItemResponse[]): number => {
  return items.reduce((prev, curr) => {
    const packNumber = curr.type === ProductType.Variation ? getPackNumber(curr.itemData?.pack) : 1;
    return prev + curr.quantity * packNumber;
  }, 0);
};

export const calculateTotalPrice = (items: CartItemResponse[]): number => {
  const filteredItems = getFilteredHalloweenPromoItems(items);
  return filteredItems.reduce((prev, curr) => prev + curr.price.price * curr.quantity, 0);
};

/**
 * Filter out system-managed coupons (free-shipping grants, halloween promo)
 * from the Order Summary display. App-exclusive coupons (APP10/APP20) are kept so
 * the discount line item in checkout remains transparent to users.
 */
export const filterSystemCoupons = (coupons: CartCouponResponse[]): CartCouponResponse[] => {
  return coupons.filter((coupon) => !COUPONS_EXEMPT_FROM_LIMIT.includes(coupon.code));
};

/**
 * Filter out hidden coupons from the list (for display in checkout coupon input area)
 * Hidden coupons are auto-applied by the rule engine and should not be shown in the coupon management UI
 */
export const filterHiddenDiscountSection = (coupons: CartCouponResponse[]): CartCouponResponse[] => {
  return coupons.filter(
    (coupon) =>
      !coupon.autoApplied && ![...COUPONS_EXEMPT_FROM_LIMIT, ...APP_EXCLUSIVE_COUPON_CODES].includes(coupon.code),
  );
};

/**
 * Transform coupon codes to their display names
 * Used for showing user-friendly coupon names instead of internal codes
 */
export const transformCouponDisplayNames = (
  coupons: CartCouponResponse[],
): Array<CartCouponResponse & { displayCode: string }> => {
  return coupons.map((coupon) => ({
    ...coupon,
    displayCode: getCouponDisplayName(coupon.code),
  }));
};

/**
 * How the order summary names a coupon and, for percentage coupons, the share of the subtotal it
 * took off. Rule-engine BMSM coupons share one label; app-exclusive codes hide their suffix.
 */
export const getCartCouponDisplay = (
  coupon: Pick<CartCouponResponse, 'code' | 'discount' | 'discountType'>,
  subtotal: number,
): { label: string; discountPercent: number } => {
  const label = coupon.code.toLowerCase().startsWith('bmsm') ? 'BMSM Coupon' : getCouponDisplayName(coupon.code);
  const hasPercentage = coupon.discountType === 'percent' && subtotal > 0 && coupon.discount > 0;
  return { label, discountPercent: hasPercentage ? Math.round((coupon.discount / subtotal) * 100) : 0 };
};

export const getCartSummary = (cart?: Cart) => {
  const items = cart?.item ?? [];
  const coupons = cart?.coupons ?? [];
  const filteredCoupons = filterSystemCoupons(coupons);

  const isFreeShipping = cart?.shippingRates?.length !== 0 && cart?.shippingRates?.[0]?.price === 0;

  return {
    items,
    coupons,
    filteredCoupons,
    orderTotal: cart?.totals.total ?? 0,
    totalTax: cart?.totals.totalCustom ?? 0,
    discountTotal: cart?.totals.totalDiscount ?? 0,
    totalQuantity: calculateTotalQuantity(items),
    totalPackExpandedQuantity: calculateTotalPackExpandedQuantity(items),
    totalPrice: calculateTotalPrice(items),
    isFreeShipping,
  };
};
